import { useRef } from "react";
import { Camera, Upload, Loader2 } from "lucide-react";

/**
 * Two-button photo picker. On mobile, "Take photo" opens the camera (via
 * `capture="environment"`) and "Choose files" opens the gallery / files app.
 * On desktop both paths open the same file picker.
 *
 * Why two <input>s? Because iOS Safari restricts a single <input capture>
 * to the camera only — so you need a separate non-capture input to let users
 * also pick existing photos.
 *
 * Props:
 *  - onFiles(fileList): called with a FileList when files are selected
 *  - multiple: allow multiple selection (default true)
 *  - busy: disable both buttons and show a spinner on the active one
 *  - accept: MIME filter (default "image/*")
 *  - size: "sm" | "md" (default "md"); "sm" renders compact pill buttons
 *  - testIdPrefix: optional prefix for data-testid values
 */
export default function PhotoPicker({
  onFiles,
  multiple = true,
  busy = false,
  accept = "image/*",
  size = "md",
  testIdPrefix = "photo-picker",
}) {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const handle = (e) => {
    // CRITICAL: copy the files into a regular Array BEFORE clearing the input,
    // because some browsers invalidate the FileList reference once value is reset.
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ""; // allow re-picking the same file later
    if (files.length && onFiles) onFiles(files);
  };

  const base =
    size === "sm"
      ? "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition disabled:opacity-60"
      : "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition disabled:opacity-60";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => cameraRef.current?.click()}
        disabled={busy}
        className={`${base} border-cyan-300 bg-cyan-600 text-white hover:bg-cyan-700`}
        data-testid={`${testIdPrefix}-camera-button`}
      >
        {busy ? <Loader2 className={`${size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} animate-spin`} /> : <Camera className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />}
        Take photo
      </button>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className={`${base} border-cyan-300 bg-white text-cyan-800 hover:bg-cyan-50`}
        data-testid={`${testIdPrefix}-file-button`}
      >
        <Upload className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        Choose from device
      </button>

      {/* Hidden inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="environment"
        multiple={multiple}
        className="hidden"
        onChange={handle}
        data-testid={`${testIdPrefix}-camera-input`}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.webp"
        multiple={multiple}
        className="hidden"
        onChange={handle}
        data-testid={`${testIdPrefix}-file-input`}
      />
    </div>
  );
}
